import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import type { MatterFile } from "@/lib/types";
import { useQuery } from "@tanstack/react-query";
import { LoadingSpinner, Typography } from "@/components/ui";
import { DestinationFolderPicker } from "./DestinationFolderPicker";
import { FirmDestinationFolderPicker } from "./FirmDestinationFolderPicker";
import {
  SignatureRequestDocumentPreview,
  type SignatureRequestPreviewDocument,
} from "./SignatureRequestDocumentPreview";

interface DestinationStepProps {
  matterId: string;
  /** Firm library folder picker (firm file, or global flow with doc chosen on Firm tab). */
  useFirmLibraryDestination: boolean;
  selectedDocument: SignatureRequestPreviewDocument | null;
  selectedDestinationFolderId: MatterFile["id"];
  setSelectedDestinationFolderId: (folderId: MatterFile["id"]) => void;
}

export const DestinationStep = ({
  matterId,
  useFirmLibraryDestination,
  selectedDocument,
  selectedDestinationFolderId,
  setSelectedDestinationFolderId,
}: DestinationStepProps) => {
  const matterQueryEnabled = Boolean(matterId) && !useFirmLibraryDestination;
  const { data: matterData, isLoading } = useQuery({
    ...retrieveMatterQueryOptions(matterId),
    enabled: matterQueryEnabled,
  });
  const matter = matterData?.data;

  if (useFirmLibraryDestination) {
    return (
      <div className="bg-card grid h-full min-h-0 w-full overflow-hidden lg:grid-cols-[minmax(0,1fr)_400px]">
        <div className="min-h-0 min-w-0 bg-slate-50/40">
          <SignatureRequestDocumentPreview
            matterId={matterId}
            selectedDocument={selectedDocument}
            canEditDocumentFiles={false}
            emptyTitle="No document selected."
            emptyDescription="Go back to the previous step to choose a document."
          />
        </div>
        <FirmDestinationFolderPicker
          selectedFolderId={selectedDestinationFolderId}
          setSelectedFolderId={setSelectedDestinationFolderId}
        />
      </div>
    );
  }

  if (!matterQueryEnabled) {
    return (
      <div className="bg-card grid h-full min-h-0 w-full overflow-hidden lg:grid-cols-[minmax(0,1fr)_400px]">
        <div className="min-h-0 min-w-0 bg-slate-50/40">
          <SignatureRequestDocumentPreview
            matterId={matterId}
            selectedDocument={selectedDocument}
            canEditDocumentFiles={false}
            emptyTitle="No document selected."
            emptyDescription="Go back to the previous step to choose a document."
          />
        </div>
        <div className="border-border/80 bg-card flex h-full min-h-0 flex-col items-center justify-center border-l px-6 text-center">
          <Typography className="text-muted-foreground text-sm">
            Select a matter first to choose a destination folder in that matter.
          </Typography>
        </div>
      </div>
    );
  }

  if (isLoading || !matter) {
    return (
      <div className="flex h-full min-h-0 w-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="bg-card grid h-full min-h-0 w-full overflow-hidden lg:grid-cols-[minmax(0,1fr)_400px]">
      <div className="min-h-0 min-w-0 bg-slate-50/40">
        <SignatureRequestDocumentPreview
          matterId={matterId}
          selectedDocument={selectedDocument}
          canEditDocumentFiles={false}
          emptyTitle="No document selected."
          emptyDescription="Go back to the previous step to choose a document."
        />
      </div>

      <DestinationFolderPicker
        matter={matter}
        selectedFolderId={selectedDestinationFolderId}
        setSelectedFolderId={setSelectedDestinationFolderId}
      />
    </div>
  );
};
